/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2023 Open Assessment Technologies SA ;
 */

import { __ } from '@oat-sa-private/ui-core';

/**
 * Definition of a keyboard for a scientific calculator.
 * @type {keyboardDefinition}
 */
export default [
    // *** ROW 1 ***
    [
        {
            type: 'switch',
            name: 'degreeMode',
            states: [
                {
                    label: 'DEG',
                    ariaLabel: __('units currently in degrees'),
                    value: true
                },
                {
                    label: 'RAD',
                    ariaLabel: __('units currently in radians'),
                    value: false
                }
            ],
            size: 8,
            tabindex: 0
        },
        {
            type: 'button',
            style: 'command',
            command: 'historyUp',
            label: 'UP',
            size: 2,
            keys: ['arrowup'],
            tabindex: 1
        },
        {
            type: 'button',
            style: 'command',
            command: 'historyDown',
            label: 'DOWN',
            size: 2,
            keys: ['arrowdown'],
            tabindex: 2
        }
    ],
    // *** ROW 2 ***
    [
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: 'SIN',
            label: 'SIN',
            size: 2,
            keys: ['sin'],
            tabindex: 3
        },
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: 'COS',
            label: 'COS',
            size: 2,
            keys: ['cos'],
            tabindex: 4
        },
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: 'TAN',
            label: 'TAN',
            size: 2,
            keys: ['tan'],
            tabindex: 5
        },
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: 'ASIN',
            label: 'ASIN',
            size: 2,
            keys: ['asin'],
            tabindex: 6
        },
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: 'ACOS',
            label: 'ACOS',
            size: 2,
            keys: ['acos'],
            tabindex: 7
        },
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: 'ATAN',
            label: 'ATAN',
            size: 2,
            keys: ['atan'],
            tabindex: 8
        }
    ],
    // *** ROW 3 ***
    [
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: 'PI',
            label: 'PI',
            size: 2,
            keys: ['pi'],
            tabindex: 9
        },
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: 'FAC',
            label: 'FAC',
            size: 2,
            keys: ['fac'],
            tabindex: 10
        },
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: 'POW NUM2',
            label: 'POW2',
            size: 2,
            tabindex: 11
        },
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: 'POW NUM3',
            label: 'POW3',
            size: 2,
            tabindex: 12
        },
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: 'POW',
            label: 'POW',
            size: 2,
            keys: ['^'],
            tabindex: 13
        },
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: 'POW NEG NUM1',
            label: 'POWMINUSONE',
            size: 2,
            tabindex: 14
        }
    ],
    // *** ROW 4 ***
    [
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: 'SQRT',
            label: 'SQRT',
            size: 2,
            keys: ['sqrt'],
            tabindex: 15
        },
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: 'CBRT',
            label: 'CBRT',
            size: 2,
            keys: ['cbrt'],
            tabindex: 16
        },
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: '@NTHRT',
            label: 'NTHRT',
            size: 2,
            tabindex: 17
        },
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: 'TEN POW',
            label: 'POW10',
            size: 2,
            tabindex: 18
        },
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: 'LOG10',
            label: 'LOG',
            size: 2,
            tabindex: 19
        },
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: 'LN',
            label: 'LN',
            keys: ['ln'],
            size: 2,
            tabindex: 20
        }
    ],
    // *** ROW 5 ***
    [
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: 'EXP',
            label: 'EXPX',
            size: 2,
            tabindex: 21
        },
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: 'E',
            label: 'E',
            keys: ['e'],
            size: 2,
            tabindex: 22
        },
        {
            type: 'button',
            style: 'function',
            command: 'sign',
            label: 'SIGN',
            size: 2,
            keys: ['±'],
            tabindex: 23
        },
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: 'PERCENT',
            label: 'PERCENT',
            size: 2,
            keys: ['%'],
            tabindex: 24
        },
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: 'LPAR',
            label: 'LPAR',
            size: 2,
            keys: ['('],
            tabindex: 25
        },
        {
            type: 'button',
            style: 'function',
            command: 'term',
            parameter: 'RPAR',
            label: 'RPAR',
            keys: [')'],
            size: 2,
            tabindex: 26
        }
    ],
    // *** ROW 6 ***
    [
        {
            type: 'button',
            style: 'command',
            command: 'deleteLeft',
            label: 'DEL',
            size: 3,
            keys: ['backspace', 'delete'],
            tabindex: 27
        },
        {
            type: 'button',
            style: 'command',
            command: 'clear',
            label: 'CLEAR',
            size: 3,
            tabindex: 28
        },
        {
            type: 'button',
            style: 'command',
            command: 'var',
            parameter: 'ans',
            label: 'ANS',
            size: 3,
            tabindex: 29
        },
        {
            type: 'button',
            style: 'operator',
            command: 'term',
            parameter: 'DIV',
            label: 'DIV',
            size: 3,
            keys: ['÷', '/'],
            tabindex: 30
        }
    ],
    // *** ROW 7 ***
    [
        {
            type: 'button',
            style: 'digit',
            command: 'term',
            parameter: 'NUM7',
            label: 'NUM7',
            size: 3,
            keys: ['7'],
            tabindex: 43
        },
        {
            type: 'button',
            style: 'digit',
            command: 'term',
            parameter: 'NUM8',
            label: 'NUM8',
            size: 3,
            keys: ['8'],
            tabindex: 44
        },
        {
            type: 'button',
            style: 'digit',
            command: 'term',
            parameter: 'NUM9',
            label: 'NUM9',
            size: 3,
            keys: ['9'],
            tabindex: 45
        },
        {
            type: 'button',
            style: 'operator',
            command: 'term',
            parameter: 'MUL',
            label: 'MUL',
            size: 3,
            keys: ['×', '*'],
            tabindex: 31
        }
    ],
    // *** ROW 8 ***
    [
        {
            type: 'button',
            style: 'digit',
            command: 'term',
            parameter: 'NUM4',
            label: 'NUM4',
            size: 3,
            keys: ['4'],
            tabindex: 40
        },
        {
            type: 'button',
            style: 'digit',
            command: 'term',
            parameter: 'NUM5',
            label: 'NUM5',
            size: 3,
            keys: ['5'],
            tabindex: 41
        },
        {
            type: 'button',
            style: 'digit',
            command: 'term',
            parameter: 'NUM6',
            label: 'NUM6',
            size: 3,
            keys: ['6'],
            tabindex: 42
        },
        {
            type: 'button',
            style: 'operator',
            command: 'term',
            parameter: 'SUB',
            label: 'SUB',
            size: 3,
            keys: ['-'],
            tabindex: 32
        }
    ],
    // *** ROW 9 ***
    [
        {
            type: 'button',
            style: 'digit',
            command: 'term',
            parameter: 'NUM1',
            label: 'NUM1',
            size: 3,
            keys: ['1'],
            tabindex: 37
        },
        {
            type: 'button',
            style: 'digit',
            command: 'term',
            parameter: 'NUM2',
            label: 'NUM2',
            size: 3,
            keys: ['2'],
            tabindex: 38
        },
        {
            type: 'button',
            style: 'digit',
            command: 'term',
            parameter: 'NUM3',
            label: 'NUM3',
            size: 3,
            keys: ['3'],
            tabindex: 39
        },
        {
            type: 'button',
            style: 'operator',
            command: 'term',
            parameter: 'ADD',
            label: 'ADD',
            size: 3,
            keys: ['+'],
            tabindex: 33
        }
    ],
    // *** ROW 10 ***
    [
        {
            type: 'button',
            style: 'digit',
            command: 'term',
            parameter: 'NUM0',
            label: 'NUM0',
            size: 3,
            keys: ['0'],
            tabindex: 36
        },
        {
            type: 'button',
            style: 'digit',
            command: 'term',
            parameter: 'DOT',
            label: 'DOT',
            size: 3,
            keys: ['.', ','],
            tabindex: 35
        },
        {
            type: 'button',
            style: 'operator',
            command: 'execute',
            label: 'EXECUTE',
            size: 6,
            keys: ['=', 'enter'],
            tabindex: 34
        }
    ]
];
